/******************************************************************************
 *
 * FILE:
 * src/modules/wms/workspace/approval/pages/ApprovalWorkspaceListPage.jsx
 *
 * MODULE:
 * Work Management System (WMS)
 *
 * FEATURE:
 * Approval Workspace
 *
 * DESCRIPTION:
 * Landing page for approvers.
 *
 ******************************************************************************/

import React, {
    useCallback,
    useEffect,
    useState,
} from "react";

import { useNavigate } from "react-router-dom";

import ExecutorWorkspaceKpiBar
    from "../../executor/components/ExecutorWorkspaceKpiBar";

import ResumeWorkCard
    from "../../executor/components/ResumeWorkCard";

import PriorityWorkCard
    from "../../executor/components/PriorityWorkCard";

import ExecutorWorkspaceFilterBar
    from "../../executor/components/ExecutorWorkspaceFilterBar";

import ExecutorWorkspaceTable
    from "../../executor/components/ExecutorWorkspaceTable";

import approvalWorkspaceService
    from "../services/approvalWorkspaceService";

import CommentsModal
    from "../../components/CommentsModal";
export default function ApprovalWorkspaceListPage() {

    // ==========================================================
    // STATE
    // ==========================================================

    const [loading, setLoading] =
        useState(false);

    const [summary, setSummary] =
        useState({});

    const [resumeWork, setResumeWork] =
        useState(null);

    const [priorityWork, setPriorityWork] =
        useState(null);

    const [rows, setRows] =
        useState([]);

    const [filters, setFilters] =
        useState({});

    const [activeKpi, setActiveKpi] =
        useState("");

    const navigate = useNavigate();
    const [showComments, setShowComments] =
    useState(false);

    const [selectedWork, setSelectedWork] =
        useState(null);

    const [commentWorkspace, setCommentWorkspace] =
    useState(null);
    
    async function handleComments(work) {

        try {

            setSelectedWork(work);

            const workspace =
                await approvalWorkspaceService
                    .getApprovalWorkspaceDetails(work.id);

            setCommentWorkspace(workspace);

            setShowComments(true);

        }

        catch (error) {

            console.error(
                "Failed to load comments",
                error
            );

        }

    }
    
    // ==========================================================
    // LOAD WORKSPACE
    // ==========================================================

    const loadWorkspace =
        useCallback(async () => {

            try {

                setLoading(true);

                const result =
                    await approvalWorkspaceService
                        .getApprovalWorkspace(filters);

                setSummary(
                    result.summary || {}
                );

                const works = result.pending_approvals || [];

                setRows(works);

                setResumeWork(
                    works.length ? works[0] : null
                );

                setPriorityWork(
                    works.length ? works[0] : null
                );

            }

            catch (err) {

                console.error(
                    "Failed to load Approval Workspace",
                    err
                );

                setSummary({});
                setResumeWork(null);
                setPriorityWork(null);
                setRows([]);

            }

            finally {

                setLoading(false);

            }

        }, [filters]);

    useEffect(() => {

        loadWorkspace();

    }, [loadWorkspace]);

    // ==========================================================
    // REFRESH
    // ==========================================================

    async function handleRefresh() {

        await loadWorkspace();

    }

    // ==========================================================
    // KPI FILTER
    // ==========================================================

    function handleKpiSelect(status) {

        setActiveKpi(status);

        setFilters((prev) => ({

            ...prev,

            status,

        }));

    }

    // ==========================================================
    // RESET FILTERS
    // ==========================================================

    function handleReset() {

        setActiveKpi("");

        setFilters({});

    }

    // ==========================================================
    // OPEN APPROVAL WORKSPACE
    // ==========================================================

    function handleOpenApproval(workId) {

        navigate(`/wms/works/${workId}/approval`);

    }

    // ==========================================================
    // RENDER
    // ==========================================================

    return (

        <div className="container-fluid">

            {/* ====================================================== */}
            {/* HEADER */}
            {/* ====================================================== */}

            <div
                className="card border-0 mb-4"
                style={{
                    background:
                        "linear-gradient(90deg,#198754,#20c997)",
                    borderRadius: 12,
                    color: "#fff",
                }}
            >

                <div
                    className="
                        card-body
                        d-flex
                        justify-content-between
                        align-items-center
                    "
                >

                    <div>

                        <h2 className="mb-1">

                            Approval Workspace

                        </h2>

                        <div>

                            Review, approve, reject or send back completed work.

                        </div>

                    </div>

                    <button
                        className="btn btn-light"
                        onClick={handleRefresh}
                        disabled={loading}
                    >

                        <i className="fas fa-sync-alt me-2" />

                        {loading
                            ? "Refreshing..."
                            : "Refresh"}

                    </button>

                </div>

            </div>

            {/* ====================================================== */}
            {/* KPI */}
            {/* ====================================================== */}

            <ExecutorWorkspaceKpiBar

                summary={summary}

                activeFilter={activeKpi}

                onSelect={handleKpiSelect}

            />

            {/* ====================================================== */}
            {/* FOCUS CARDS */}
            {/* ====================================================== */}

            <div className="row mb-4">

                <div className="col-lg-6 mb-3">

                    <ResumeWorkCard

                        work={resumeWork}

                        onExecute={handleOpenApproval}

                    />

                </div>

                <div className="col-lg-6 mb-3">

                    <PriorityWorkCard

                        work={priorityWork}

                        onExecute={handleOpenApproval}

                    />

                </div>

            </div>

            {/* ====================================================== */}
            {/* FILTER BAR */}
            {/* ====================================================== */}

            <ExecutorWorkspaceFilterBar

                filters={filters}

                onChange={setFilters}

                onReset={handleReset}

            />

            {/* ====================================================== */}
            {/* TABLE */}
            {/* ====================================================== */}

            <ExecutorWorkspaceTable

                rows={rows}

                loading={loading}

                onExecute={handleOpenApproval}
                 onComments={handleComments}

            />

        <CommentsModal
            show={showComments}
            onClose={() => {

                setShowComments(false);

                setSelectedWork(null);

                setCommentWorkspace(null);

            }}
            comments={commentWorkspace?.comments || []}
            onAddComment={async (text) => {

                await approvalWorkspaceService.addComment(

                    selectedWork.id,

                    text

                );

                const workspace =
                    await approvalWorkspaceService
                        .getApprovalWorkspaceDetails(
                            selectedWork.id
                        );

                setCommentWorkspace(workspace);

            }}
            title={
                selectedWork
                    ? `Comments - ${selectedWork.work_no}`
                    : "Comments"
            }
        />
        </div>

    );

}